import {
  type Dispatch,
  type SetStateAction,
  createContext,
  useCallback,
  useContext,
  useState,
} from "react";

export interface OnboardingStepContextType {
  currentStep: number;
  totalSteps: number;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  jumpToStep: (step: number) => void;
  setTotalSteps: Dispatch<SetStateAction<number>>;
  isModal: boolean;
  closeModal: () => void;
}

export const OnboardingStepContext = createContext<OnboardingStepContextType>({
  currentStep: 0,
  totalSteps: 0,
  goToNextStep: () => {},
  goToPreviousStep: () => {},
  jumpToStep: () => {},
  setTotalSteps: () => {},
  isModal: false,
  closeModal: () => {},
});

export const OnboardingStepProvider = ({
  isModal = false,
  closeModal = () => {},
  children,
}: {
  isModal?: boolean;
  closeModal?: () => void;
  children: React.ReactNode;
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [totalSteps, setTotalSteps] = useState(0);

  const goToNextStep = useCallback(() => {
    setCurrentStep(currentStep + 1);
  }, [currentStep]);

  const goToPreviousStep = useCallback(() => {
    setCurrentStep(currentStep - 1);
  }, [currentStep]);

  const jumpToStep = useCallback((step: number) => {
    setCurrentStep(step);
  }, []);

  return (
    <OnboardingStepContext.Provider
      value={{
        currentStep,
        totalSteps,
        goToNextStep,
        goToPreviousStep,
        jumpToStep,
        setTotalSteps,
        isModal,
        closeModal,
      }}
    >
      {children}
    </OnboardingStepContext.Provider>
  );
};

export function useOnboardingStep(): OnboardingStepContextType {
  const context = useContext(OnboardingStepContext);
  if (!context) {
    throw new Error(
      "useOnboardingStep must be used within an OnboardingStepProvider",
    );
  }
  return context;
}
